"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useMutation } from "@tanstack/react-query";
import { GitPullRequestIcon, LoaderIcon } from "lucide-react";
import { toast } from "sonner";

import type { RouterInputs } from "@acme/api";
import type { PackageMetadataType } from "@acme/validators";
import { Button } from "@acme/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@acme/ui/tooltip";
import { StudioMode } from "@acme/validators";

import { useTRPC } from "~/trpc/react";
import { useStudioStore } from "../../_hooks/use-studio-store";
import { CreatePackageDialog } from "./create-package-dialog";

export function CreatePRButton() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const trpc = useTRPC();

  const getCurrentState = useStudioStore((state) => state.getCurrentState);
  const language = useStudioStore((state) => state.lang);
  const mode = useStudioStore((state) => state.mode);
  const jsAstGrepCodemod = useStudioStore((state) => state.jsAstGrepCodemod);
  const repositories = useStudioStore((state) => state.selectedRepositories);

  const router = useRouter();

  const { mutateAsync: createPackage, isPending: isCreating } = useMutation(
    trpc.studio.createPackageFromStudio.mutationOptions({
      onSuccess: (data) => {
        router.push(
          `/instances/${data.appInstanceId}/projects/${data.projectId}`,
        );
      },
      onError: (error) => {
        toast.error("Failed to create package", {
          description: error.message,
        });
      },
    }),
  );

  const handleCreatePackage = async (packageMetadata: PackageMetadataType) => {
    try {
      if (!repositories || repositories.length === 0) {
        toast.error("No repositories found", {
          description: "Please select a repository to create a campaign.",
        });
        return;
      }

      const currentState = getCurrentState();

      const studioConfig = {
        mode,
        config: {
          config: currentState.config, // YAML config string
          language,
          patterns: [],
        },
        jsAstGrepCodemod: {
          script: jsAstGrepCodemod,
          language,
        },
        metadata: {
          name: packageMetadata.name,
          description: packageMetadata.description,
          version: "1.0.0",
        },
      } as RouterInputs["studio"]["createPackageFromStudio"]["studioConfig"];

      await createPackage({
        studioConfig,
        packageMetadata,
        repositories,
      });

      setIsDialogOpen(false);
    } catch (error) {
      console.error("Failed to create package:", error);
    }
  };

  const handleOpenDialog = () => {
    const currentState = getCurrentState();

    if (mode === StudioMode.Config) {
      if (!currentState.config.trim()) {
        toast.error("Please create some rules first", {
          description:
            "You need to have at least one AST-grep rule configured.",
        });
        return;
      }
    } else {
      if (!jsAstGrepCodemod.trim()) {
        toast.error("Please add some codemod logic", {
          description: "Your codemod needs some transformation logic.",
        });
        return;
      }
    }

    setIsDialogOpen(true);
  };

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            onClick={handleOpenDialog}
            disabled={isCreating}
          >
            {isCreating ? (
              <LoaderIcon className="size-4 animate-spin" />
            ) : (
              <GitPullRequestIcon className="size-4" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Create PR</p>
          <p className="text-muted-foreground text-xs">
            Package your codemod and create a pull request
          </p>
        </TooltipContent>
      </Tooltip>

      <CreatePackageDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSubmit={handleCreatePackage}
        isLoading={isCreating}
      />
    </>
  );
}
